import {
    DotsHorizontalIcon,
    Link2Icon,
} from '@radix-ui/react-icons';
import {
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import { useContext, useState } from 'react';
import { Link } from 'react-router';
import '../../App.css';
import { AuthContext } from '../../context/AuthContext';
import CancelCompetitionSportClassDialog from '../dialogs/cancelCompetitionSportClassDialog';
import ChangeCompetitionSportClassDateDialog from '../dialogs/changeCompetitionSportClassDateDialog';
import ConnectCupDialog from '../dialogs/connectCupDialog';
import DeleteCompetitionSportClassDialog from '../dialogs/deleteCompetitionSportClassDialog';
import DisconnectCupDialog from '../dialogs/disconnectCupDialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { DataTable } from './datatable';
import DatatableSortButton from './datatableSortButton';

export const columns = (fetchCompetition, competition, auth, singleCup, setSelectedSportClass, setDialogOpen, dataLength) => {
    const isAdminOrPrivilegedUser = auth?.user?.superadmin || (auth?.user?.roleInClub == 'admin' && auth?.user?.memberOfClub == parseInt(competition.club.id));

    const baseColumns = [
        {
            accessorKey: "name",
            header: ({ column }) => <DatatableSortButton column={column}>Klass</DatatableSortButton>,
            cell: info => {
                const sportClass = info.row.original;
                return (
                    <>
                        <Link
                            to={`/competition/${sportClass.competition_id}/class/${sportClass.id}`}
                            className="text-blue-600 font-medium hover:underline mr-2"
                        >
                            {sportClass.name} {info.row.original.assessment && (
                                <> - Bedömning {info.row.original.assessment}</>
                            )}
                        </Link>
                        {sportClass.canceled && <Badge variant="destructive">Inställd</Badge>}
                        {sportClass.finished_at && <Badge variant="secondary">Avslutad</Badge>}
                    </>
                );
            }
        },
        {
            accessorKey: "date",
            header: ({ column }) => <DatatableSortButton column={column}>Datum</DatatableSortButton>,
            cell: info => {
                const sportClass = info.row.original;
                const date = new Date(sportClass.date);
                return date.toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit"
                });
            }
        },
        {
            accessorKey: "registrations",
            header: ({ column }) => <DatatableSortButton column={column}>Antal starter</DatatableSortButton>,
        },
    ];

    if (isAdminOrPrivilegedUser && dataLength > 0) {
        baseColumns.push(
            {
                id: "actions",
                enableHiding: false,
                cell: ({ row }) => {
                    const sportClass = row.original;

                    // Hide the actions column for rows with registrations
                    if (singleCup && sportClass.registrations > 0) {
                        return null; // Return nothing for rows with registrations
                    }

                    return (
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" className="h-8 w-8 p-0 float-right">
                                    <span className="sr-only">Öppna meny</span>
                                    <DotsHorizontalIcon className="h-4 w-4" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                                align="end"
                                onCloseAutoFocus={(e) => {
                                    e.preventDefault();
                                }}
                            >
                                <DropdownMenuLabel>Alternativ</DropdownMenuLabel>
                                <DropdownMenuSeparator />
                                {/* Condition to allow datatable to be used in competition single and cup single */}
                                {!singleCup ? (
                                    <>
                                        {/* Inside competition single */}
                                        {(!sportClass.canceled && sportClass.cup_id == null && !sportClass?.split && !(sportClass?.name.toLowerCase().includes('längd') || sportClass?.name.toLowerCase().includes('höjd'))) && (
                                            <DropdownMenuItem
                                                onClick={() => {
                                                    setSelectedSportClass(sportClass); // Store selected class
                                                    setDialogOpen(true); // Open the dialog
                                                }}
                                                className='cursor-pointer'
                                            >
                                                <Link2Icon className='mr-3' />
                                                Koppla till en cup
                                            </DropdownMenuItem>
                                        )}
                                        <ChangeCompetitionSportClassDateDialog
                                            sportClass={sportClass}
                                            fetchCompetition={fetchCompetition}
                                        />
                                        <DropdownMenuSeparator />
                                        {!sportClass.canceled &&
                                            <CancelCompetitionSportClassDialog
                                                sportClass={sportClass}
                                                fetchCompetition={fetchCompetition}
                                            />
                                        }
                                        <DeleteCompetitionSportClassDialog
                                            sportClass={sportClass}
                                            fetchCompetition={fetchCompetition}
                                        />
                                    </>
                                ) : (
                                    <>
                                        {/* Inside cup single */}
                                        <DisconnectCupDialog
                                            sportClass={sportClass}
                                            fetchCompetition={fetchCompetition}
                                        />
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    );
                }
            }
        );
    }

    return baseColumns;
};

export function CompetitionSportClassesDatatable({ sportClasses, isLoading, fetchCompetition, competition, singleCup }) {
    const [sorting, setSorting] = useState([]);
    const [columnFilters, setColumnFilters] = useState([]);
    const { auth } = useContext(AuthContext)

    const [selectedSportClass, setSelectedSportClass] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const table = useReactTable({
        data: sportClasses,
        columns: columns(fetchCompetition, competition, auth, singleCup, setSelectedSportClass, setDialogOpen, sportClasses.length),
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        onColumnFiltersChange: setColumnFilters,
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            sorting,
            columnFilters,
        },
    });

    return (
        <>
            {/* <div className='grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 py-3'>
                <div className='flex gap-3'>
                </div>
                <div className='grid grid-cols-2 xl:col-start-3 gap-3'>
                    <Button className='grid lg:col-start-2' onClick={() => navigate(`/add-bunny`)}>
                        Lägg till kanin
                    </Button>
                </div>
            </div> */}
            {selectedSportClass && (
                <ConnectCupDialog
                    sportClass={selectedSportClass}
                    fetchCompetition={fetchCompetition}
                    competitionId={competition.id}
                    isDialogOpen={dialogOpen}
                    setIsDialogOpen={setDialogOpen} // Pass dialog state control from the parent
                />
            )}
            <DataTable
                table={table}
                isLoading={isLoading}
                columns={columns()}
            />
        </>
    );
}
